import { Component, TemplateRef, ViewChild } from '@angular/core';
import { Subscription, switchMap } from 'rxjs';
import { map, filter, distinctUntilChanged } from 'rxjs/operators';

import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { OptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';

import { SearchResultsService } from '../../../Monitor/Core/services/search-results.service';
import { ViRoutingService } from '../../services/vi-routing.service';
import { ViMonitorService } from '../../services/vi-monitor.service';
import { ViRouteModel } from '../../models/vi-route.model';
import { ViMonitor } from '../../models/vi-monitor.model';
import { ViResultsModeEnum } from '../../enums/vi-results-mode.enum';
import { ViRouteType } from '../../enums/vi-route-type.enum';
import { ViMonitorStatusEnum } from '../../enums/vi-monitor-status.enum';

@Component({
    selector: 'agpr-vi-results-shell',
    templateUrl: './vi-results-shell.component.html',
    styleUrl: './vi-results-shell.component.scss',
    standalone: false
})
export class ViResultsShellComponent {
    private static readonly MODE_BY_ROUTE: { [key: string]: ViResultsModeEnum } = {
        [ViRouteType.REPORTS]: ViResultsModeEnum.ANALYTICS,
        [ViRouteType.QUERIES_AND_ANSWERS]: ViResultsModeEnum.QUERIES
    };

    selectedViewMode: ViResultsModeEnum;

    monitor: ViMonitor | null = null;
    remainingActiveQuestionQuota: number = 0;
    hasInsufficientQuota: boolean = false;

    @ViewChild('activateConfirmContent') activateConfirmContent?: TemplateRef<any>;
    @ViewChild('insufficientQuotaContent') insufficientQuotaContent?: TemplateRef<any>;

    private subscriptions: Subscription[] = [];

    readonly id = 'vi-results';
    readonly buttonType = ButtonTypesEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly monitorStatus = ViMonitorStatusEnum;
    readonly viewModeOptions = [
        new OptionModel('VisibilityIntelligence.reports.analytics', ViResultsModeEnum.ANALYTICS),
        new OptionModel('VisibilityIntelligence.reports.queries', ViResultsModeEnum.QUERIES)
    ];

    constructor(
        private routingService: ViRoutingService,
        private searchResultsService: SearchResultsService,
        private viMonitorService: ViMonitorService,
        private coreDialogService: CoreDialogService
    ) {
        this.subscriptions.push(
            this.viMonitorService.currentResource$.pipe(
                switchMap(resource => {
                    this.monitor = resource;
                    return this.viMonitorService.getRemainingActiveQuestionQuota$();
                })
            ).subscribe(remainingQuota => {
                this.remainingActiveQuestionQuota = remainingQuota;
                this.hasInsufficientQuota = (this.monitor?.questions?.length ?? 0) > this.remainingActiveQuestionQuota;
            }),
            this.routingService.getCurrentRoute().pipe(
                filter((route: ViRouteModel) => ViResultsShellComponent.MODE_BY_ROUTE[route?.type] !== undefined),
                map((route: ViRouteModel) => ViResultsShellComponent.MODE_BY_ROUTE[route?.type]),
                filter(mode => mode !== undefined),
                distinctUntilChanged()
            ).subscribe((mode: ViResultsModeEnum) => {
                const isChange = this.selectedViewMode !== undefined && this.selectedViewMode !== mode;

                this.selectedViewMode = mode;
                this.searchResultsService.setCurrentViewMode(mode as any);
                this.searchResultsService.setIsViewModeChange(isChange);
            })
        );
    }

    /**
     * Navigates to the proper route only, subscriptions above updates selectedViewMode
     * and the search service when the navigation lands.
     */
    viewModeChanged(mode: ViResultsModeEnum) {
        if (mode === this.selectedViewMode || !this.monitor?.id) {
            return;
        }

        if (mode === ViResultsModeEnum.ANALYTICS) {
            this.routingService.gotoReports(this.monitor.id);
        } else {
            this.routingService.gotoQnA(this.monitor.id);
        }
    }

    edit(): void {
        if (this.monitor?.id) {
            this.routingService.gotoEdit(this.monitor.id);
        }
    }

    activate(): void {
        if (!this.monitor?.id) {
            return;
        }

        if (this.hasInsufficientQuota) {
            this.coreDialogService.open(ConfirmDialogComponent, {
                inputs: {
                    id: 'viResultsShell_insufficientQuotaConfirm',
                    heading: 'VisibilityIntelligence.monitor.activate.insufficientQuota.title',
                    contentTemplate: this.insufficientQuotaContent,
                    showCancel: false,
                    okLabel: 'VisibilityIntelligence.reports.acknowledge',
                    maxWidth: 510
                },
                outputs: {
                    dialogClose: () => {}
                }
            });
            return;
        }

        const monitorId = this.monitor.id;
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'viResultsShell_activateConfirm',
                heading: 'VisibilityIntelligence.monitorSetup.activateConfirm.title',
                contentTemplate: this.activateConfirmContent,
                okLabel: 'activate',
                maxWidth: 510,
                confirmAction: () => this.viMonitorService.activate(monitorId)
            },
            outputs: {
                dialogClose: () => {}
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    pause(): void {
        if (!this.monitor?.id) {
            return;
        }

        this.subscriptions.push(
            this.viMonitorService.pause(this.monitor.id).subscribe()
        );
    }
}
